import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Mail, Clock, AlertTriangle, RefreshCw, Sun, Moon } from 'lucide-react';
import { GUEST_TOKEN_KEY } from '../context/AuthContext';
import Sends from './Sends';
import Queue from './Queue';

type Tab = 'sends' | 'queue';

// Links with expiry > 5 years from now are treated as permanent
const PERMANENT_THRESHOLD_MS = 5 * 365 * 24 * 60 * 60 * 1000;

function decodeGuestToken(token: string): { exp: number; scope: string[] } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { exp: payload.exp ?? 0, scope: payload.scope ?? [] };
  } catch {
    return null;
  }
}

function formatCountdown(msLeft: number): string {
  if (msLeft <= 0) return 'Expired';
  const totalSecs = Math.ceil(msLeft / 1000);
  const hours = Math.floor(totalSecs / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;
  if (hours > 0) return `${hours}h ${mins.toString().padStart(2, '0')}m`;
  if (mins > 0) return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  return `${secs}s`;
}

export default function GuestView() {
  const [token, setToken] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [msLeft, setMsLeft] = useState<number>(0);
  const [isPermanent, setIsPermanent] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('sends');
  const [darkMode, setDarkMode] = useState(true);

  const applyToken = (raw: string) => {
    const info = decodeGuestToken(raw);
    if (!info) {
      setInvalid(true);
      return;
    }
    const remaining = info.exp * 1000 - Date.now();
    if (remaining <= 0) {
      setExpired(true);
      return;
    }
    sessionStorage.setItem(GUEST_TOKEN_KEY, raw);
    setToken(raw);
    setMsLeft(remaining);
    setIsPermanent(remaining > PERMANENT_THRESHOLD_MS);
  };

  // Exchange /guest?g=<id> (or legacy ?token=) then strip the query string
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const linkId = params.get('g');
    const raw = params.get('token');

    const stripQuery = () => window.history.replaceState({}, '', window.location.pathname);

    if (linkId) {
      void axios
        .post<{ token: string }>('/api/auth/guest-session', { id: linkId })
        .then((res) => {
          stripQuery();
          applyToken(res.data.token);
        })
        .catch(() => {
          stripQuery();
          setInvalid(true);
        });
      return;
    }

    if (raw) {
      stripQuery();
      applyToken(raw);
      return;
    }

    const stored = sessionStorage.getItem(GUEST_TOKEN_KEY);
    if (stored) {
      applyToken(stored);
      return;
    }
    setInvalid(true);
  }, []);

  // Dark mode: apply/remove `dark` class on <html> based on state
  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) root.classList.add('dark');
    else root.classList.remove('dark');
    return () => root.classList.remove('dark'); // cleanup on unmount
  }, [darkMode]);

  // Countdown timer (skip for permanent links)
  useEffect(() => {
    if (!token || expired || isPermanent) return;
    const interval = setInterval(() => {
      setMsLeft((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          setExpired(true);
          sessionStorage.removeItem(GUEST_TOKEN_KEY);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [token, expired, isPermanent]);

  // Clear guest token from sessionStorage on unmount
  useEffect(() => {
    return () => {
      sessionStorage.removeItem(GUEST_TOKEN_KEY);
    };
  }, []);

  const tabClass = useCallback((tab: Tab) =>
    `px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-blue-500 text-blue-500 dark:text-blue-400 dark:border-blue-400 bg-transparent'
        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'
    }`, [activeTab]);

  // ── Invalid / missing token ───────────────────────────────────────────────
  if (invalid) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow p-8 max-w-md w-full text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-500">This link is missing or invalid. Please ask your administrator for a new link.</p>
        </div>
      </div>
    );
  }

  // ── Expired ───────────────────────────────────────────────────────────────
  if (expired) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow p-8 max-w-md w-full text-center">
          <Clock className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Link Expired</h1>
          <p className="text-gray-500">This shared link has expired. Please ask your administrator to generate a new one.</p>
        </div>
      </div>
    );
  }

  // ── Loading (token not yet resolved) ─────────────────────────────────────
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-6 w-6 text-blue-500 animate-spin" />
      </div>
    );
  }

  // ── Main guest view ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors">
      {/* Minimal header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded bg-blue-600 text-white font-bold text-lg select-none">
              Z
            </div>
            <span className="font-semibold text-gray-900 dark:text-white text-sm">ZENTO Email Craft</span>
            <span className="text-gray-400 text-sm ml-1">— Read-only View</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Expiry indicator — hidden for permanent links */}
            {!isPermanent && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
              <Clock className="h-4 w-4" />
              {msLeft > 0 ? (
                <span>Expires in <span className={`font-medium ${msLeft < 60_000 ? 'text-red-600' : 'text-gray-700 dark:text-gray-200'}`}>{formatCountdown(msLeft)}</span></span>
              ) : (
                <span className="text-red-600 font-medium">Expired</span>
              )}
            </div>
            )}
            {/* Dark mode toggle */}
            <button
              onClick={() => setDarkMode((d) => !d)}
              className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </header>

      {/* Tab bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 pt-2">
          <button className={tabClass('sends')} onClick={() => setActiveTab('sends')}>
            <span className="flex items-center gap-1.5">
              <Mail className="h-4 w-4" />
              Sends
            </span>
          </button>
          <button className={tabClass('queue')} onClick={() => setActiveTab('queue')}>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              Queue
            </span>
          </button>
        </div>
      </div>

      {/* Page content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'sends' && <Sends />}
        {activeTab === 'queue' && <Queue />}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-3 text-center text-xs text-gray-400">
        ZENTO Email Craft | <a href="https://zentosaas.com" className="hover:text-gray-600" target="_blank" rel="noopener noreferrer">www.zentosaas.com</a>
      </footer>
    </div>
  );
}
